import * as React from 'react'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faRotate } from '@fortawesome/pro-duotone-svg-icons'
import { useSecureShare } from './useSecureShare'
import { StepOne } from './StepOne'
import { StepTwo } from './StepTwo'
import { StepThree } from './StepThree'
import { useCallback } from 'react'
import { toast } from 'react-toastify'
import AppDialog from '@/shared/ui/app-dialog/AppDialog'
import { StyledButton } from '@/shared/ui/buttons'

type Props = {
    kedo: any
    contentDossier: any
    closeModal: () => void
}

const ContentDossierSecureShare: React.FC<Props> = ({
    kedo,
    contentDossier,
    closeModal,
}) => {
    const { state, setPartialState } = useSecureShare({ kedo, contentDossier })

    const nextStep = () => setPartialState({ step: state.step + 1 })
    const prevStep = () => setPartialState({ step: state.step - 1 })

    const renderTitle = () => {
        const base = kedo.t('Secure share data', { ns: 'secureshare' })
        const steps = ['Recipients', 'Configuration', 'Summary']
        return `${base}: ${kedo.t(steps[state.step - 1])}`
    }

    const transformContactsForPayload = useCallback(
        async (
            recipientUsers: any[],
            dossierPersons: any[],
            customEmails: any[]
        ) => {
            const contacts: any[] = []

            for (const user of recipientUsers) {
                const res = await kedo
                    .api()
                    .get(kedo.api().getUserEndpoint(user.id))
                contacts.push({
                    email: res.data.email,
                    mobile: res.data.mobile,
                })
            }

            for (const email of customEmails || []) {
                contacts.push({ email: email.value })
            }

            for (const person of dossierPersons || []) {
                contacts.push({ email: person.email, mobile: person.mobile })
            }

            setPartialState({ contacts })
        },
        [kedo, setPartialState]
    )

    const renderCurrentStep = () => {
        switch (state.step) {
            case 1:
                return (
                    <StepOne
                        kedo={kedo}
                        state={state}
                        setPartialState={setPartialState}
                        transformContactsForPayload={
                            transformContactsForPayload
                        }
                    />
                )
            case 2:
                return (
                    <StepTwo
                        kedo={kedo}
                        state={state}
                        setPartialState={setPartialState}
                    />
                )
            case 3:
                return <StepThree kedo={kedo} state={state} />
            default:
                return null
        }
    }

    const handleSend = async () => {
        setPartialState({ processing: true, error: false })
        const secureShareUrl = kedo
            .api()
            .getSecureShareEndpoint(contentDossier.id)

        const payload = {
            activeUntil: state.shareUntilDate,
            contacts: state.contacts,
            settings: {
                share_type: state.shareType,
                share_value: state.shareTypeValue,
            },
        }

        try {
            await kedo.api().post(secureShareUrl, payload)
            toast.success(kedo.t('Information secure shared'), {
                position: 'bottom-center',
                autoClose: 2500,
            })
            setPartialState({ processing: false, error: false })
            closeModal()
        } catch {
            setPartialState({ processing: false, error: true })
        }
    }

    return (
        <AppDialog
            aria-labelledby="contained-modal-title-vcenter"
            open
            maxWidth={'lg'}
            fullWidth
            topAlign
            title={renderTitle()}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    closeModal
                }
            }}
            headerClose={closeModal}
            contentProps={{ dividers: true }}
            actions={
                <>
                    <StyledButton
                        disabled={state.processing}
                        onClick={closeModal}
                    >
                        {kedo.t('Cancel')}
                    </StyledButton>
                    {state.step > 1 && (
                        <StyledButton color="secondary" onClick={prevStep}>
                            {kedo.t('Previous')}
                        </StyledButton>
                    )}
                    {state.step <= 2 && (
                        <StyledButton
                            color="secondary"
                            onClick={nextStep}
                            disabled={
                                state.processing ||
                                (!state.contacts.length &&
                                    !state.recipientUsers.length)
                            }
                        >
                            {kedo.t('Next')}
                        </StyledButton>
                    )}
                    {state.step === 3 && (
                        <StyledButton
                            onClick={handleSend}
                            disabled={state.processing}
                        >
                            <Box component="span" sx={{ mr: 1 }}>
                                {kedo.t('Send')}
                            </Box>
                            <FontAwesomeIcon
                                icon={state.processing ? faRotate : faEnvelope}
                                spin={state.processing}
                            />
                        </StyledButton>
                    )}
                </>
            }
            // keyboard={false}
        >
            <div>
                {state.error && (
                    <Alert severity="error">
                        {kedo.t('Something went wrong')}
                    </Alert>
                )}
                {renderCurrentStep()}
            </div>
        </AppDialog>
    )
}

export default ContentDossierSecureShare
